import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Backspace, SignIn, SignOut, ForkKnife, ArrowClockwise, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/site/Logo";
import { api, BASE_URL } from "@/lib/api";

const ACTIONS = {
    entrada: { label: "Registrar Entrada", icon: SignIn, color: "bg-emerald-600 hover:bg-emerald-500" },
    comida_inicio: { label: "Salir a Comer", icon: ForkKnife, color: "bg-amber-600 hover:bg-amber-500" },
    comida_fin: { label: "Regreso de Comida", icon: ArrowClockwise, color: "bg-sky-600 hover:bg-sky-500" },
    salida: { label: "Registrar Salida", icon: SignOut, color: "bg-rose-600 hover:bg-rose-500" },
};

const fmtTime = (iso) =>
    iso ? new Date(iso).toLocaleTimeString("es-US", { hour: "numeric", minute: "2-digit" }) : "—";

function LiveClock() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);
    return (
        <div className="text-center" data-testid="clock-live">
            <p className="font-display font-black text-5xl sm:text-6xl tracking-tight tabular-nums">
                {now.toLocaleTimeString("es-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })}
            </p>
            <p className="text-white/50 text-sm mt-1 capitalize">
                {now.toLocaleDateString("es-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
        </div>
    );
}

export default function TimeClock() {
    const [pin, setPin] = useState("");
    const [state, setState] = useState(null); // null | lookup response
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(null); // {action, at}
    const [busy, setBusy] = useState(false);
    const resetTimer = useRef(null);

    const reset = () => {
        clearTimeout(resetTimer.current);
        setPin(""); setState(null); setError(""); setSuccess(null);
    };

    const lookup = async (fullPin) => {
        setBusy(true); setError("");
        try {
            const r = await api.post("/attendance/lookup", { pin: fullPin });
            setState(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "PIN no reconocido");
            setPin("");
        } finally {
            setBusy(false);
        }
    };

    const press = (d) => {
        if (busy || state) return;
        setError("");
        const next = (pin + d).slice(0, 6);
        setPin(next);
        if (next.length === 6) lookup(next);
    };

    const submitPin = () => {
        if (pin.length >= 4 && !busy) lookup(pin);
    };

    const punch = async (action) => {
        setBusy(true); setError("");
        try {
            const r = await api.post("/attendance/punch", { pin, action });
            setSuccess({ action, at: r.data.at, employee: r.data.employee, record: r.data.record });
            resetTimer.current = setTimeout(reset, 6000);
        } catch (e) {
            setError(e?.response?.data?.detail || "No se pudo registrar");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#141210] text-white flex flex-col items-center px-4 py-8" data-testid="timeclock-page">
            <div className="w-full max-w-md flex items-center justify-between mb-6">
                <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white text-xs font-bold uppercase tracking-widest" data-testid="timeclock-back">
                    <ArrowLeft size={14} weight="bold" /> Inicio
                </Link>
                <div className="flex items-center gap-2">
                    <Logo size={30} className="rounded-full bg-white" />
                    <span className="font-display font-black text-sm">J&amp;W Checador</span>
                </div>
            </div>

            <LiveClock />

            <div className="w-full max-w-md mt-8">
                {success ? (
                    <SuccessCard success={success} onDone={reset} />
                ) : state ? (
                    <ActionCard state={state} busy={busy} error={error} onPunch={punch} onCancel={reset} />
                ) : (
                    <PinPad pin={pin} error={error} busy={busy} onPress={press} onErase={() => setPin(pin.slice(0, -1))} onSubmit={submitPin} />
                )}
            </div>
        </div>
    );
}

function PinPad({ pin, error, busy, onPress, onErase, onSubmit }) {
    return (
        <div data-testid="pin-pad">
            <p className="text-center text-white/60 text-sm font-semibold mb-4">
                Ingresa tu PIN de empleado (4 a 6 dígitos)
            </p>
            <div className="flex justify-center gap-3 mb-5" data-testid="pin-dots">
                {[...Array(Math.max(4, pin.length))].map((_, i) => (
                    <span
                        key={i}
                        className={`h-4 w-4 rounded-full border-2 transition ${
                            pin.length > i ? "bg-primary border-primary" : "border-white/30"
                        }`}
                    />
                ))}
            </div>
            {error && (
                <p className="text-center text-rose-400 text-sm font-bold mb-3" data-testid="pin-error">{error}</p>
            )}
            {busy && <p className="text-center text-white/50 text-sm mb-3">Verificando...</p>}
            <div className="grid grid-cols-3 gap-3">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
                    <button
                        key={d}
                        data-testid={`pin-key-${d}`}
                        onClick={() => onPress(d)}
                        className="h-16 rounded-2xl bg-white/5 border border-white/10 font-display font-black text-2xl hover:bg-white/15 active:scale-95 transition"
                    >
                        {d}
                    </button>
                ))}
                <span />
                <button
                    data-testid="pin-key-0"
                    onClick={() => onPress("0")}
                    className="h-16 rounded-2xl bg-white/5 border border-white/10 font-display font-black text-2xl hover:bg-white/15 active:scale-95 transition"
                >
                    0
                </button>
                <button
                    data-testid="pin-erase"
                    onClick={onErase}
                    className="h-16 rounded-2xl bg-white/5 border border-white/10 grid place-items-center hover:bg-white/15 active:scale-95 transition"
                >
                    <Backspace size={24} weight="bold" />
                </button>
            </div>
            <button
                data-testid="pin-submit"
                onClick={onSubmit}
                disabled={pin.length < 4 || busy}
                className="w-full mt-3 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 font-bold uppercase tracking-widest text-sm transition active:scale-[0.98] disabled:opacity-30 disabled:cursor-not-allowed"
            >
                Entrar
            </button>
        </div>
    );
}

function ActionCard({ state, busy, error, onPunch, onCancel }) {
    const { employee, shift, today, allowed_actions } = state;
    return (
        <div className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="action-card">
            <div className="flex items-center gap-4">
                {employee.photo_url ? (
                    <img src={employee.photo_url.startsWith("/") ? `${BASE_URL}${employee.photo_url}` : employee.photo_url} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-primary" />
                ) : (
                    <div className="h-16 w-16 rounded-full bg-primary/20 border-2 border-primary grid place-items-center font-display font-black text-xl">
                        {(employee.name || "?").slice(0, 1)}
                    </div>
                )}
                <div className="min-w-0">
                    <p className="font-display font-black text-xl truncate" data-testid="action-employee-name">{employee.name}</p>
                    <p className="text-white/50 text-xs">
                        {employee.employee_number} · {employee.department || "—"} {employee.position ? `· ${employee.position}` : ""}
                    </p>
                    {shift && (
                        <p className="text-white/40 text-[11px] mt-0.5">
                            {shift.name}: {shift.entry} — {shift.exit}
                        </p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mt-5 text-center text-[11px]">
                {[
                    ["Entrada", today?.entrada_at],
                    ["Comida", today?.comida_inicio_at],
                    ["Regreso", today?.comida_fin_at],
                    ["Salida", today?.salida_at],
                ].map(([l, v]) => (
                    <div key={l} className="bg-white/5 rounded-xl py-2">
                        <p className="text-white/40 uppercase tracking-wider">{l}</p>
                        <p className="font-bold mt-0.5 tabular-nums">{fmtTime(v)}</p>
                    </div>
                ))}
            </div>

            {error && <p className="text-center text-rose-400 text-sm font-bold mt-4" data-testid="action-error">{error}</p>}

            <div className="space-y-2.5 mt-5">
                {allowed_actions.length === 0 ? (
                    <p className="text-center text-emerald-400 font-bold text-sm py-2" data-testid="day-closed">
                        Tu jornada de hoy está completa. ¡Buen trabajo! 🎉
                    </p>
                ) : (
                    allowed_actions.map((a) => {
                        const cfg = ACTIONS[a];
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={a}
                                data-testid={`punch-${a}`}
                                disabled={busy}
                                onClick={() => onPunch(a)}
                                className={`w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl font-bold uppercase tracking-widest text-sm transition active:scale-[0.98] disabled:opacity-50 ${cfg.color}`}
                            >
                                <Icon size={20} weight="bold" />
                                {cfg.label}
                            </button>
                        );
                    })
                )}
                <button
                    data-testid="punch-cancel"
                    onClick={onCancel}
                    className="w-full py-3 rounded-2xl border border-white/15 text-white/60 hover:text-white font-bold uppercase tracking-widest text-xs transition"
                >
                    Cancelar
                </button>
            </div>
        </div>
    );
}

function SuccessCard({ success, onDone }) {
    const cfg = ACTIONS[success.action];
    return (
        <div className="bg-white/5 border border-emerald-500/40 rounded-3xl p-8 text-center" data-testid="punch-success">
            <CheckCircle size={64} weight="fill" className="mx-auto text-emerald-400" />
            <p className="font-display font-black text-2xl mt-3">{cfg.label}</p>
            <p className="text-white/60 mt-1">{success.employee.name}</p>
            <p className="font-display font-black text-4xl mt-3 tabular-nums">{fmtTime(success.at)}</p>
            {success.record?.is_late && success.action === "entrada" && (
                <p className="mt-2 text-amber-400 text-sm font-bold" data-testid="late-warning">
                    Retardo de {success.record.delay_minutes} min
                </p>
            )}
            {success.action === "salida" && success.record?.worked_minutes != null && (
                <p className="mt-2 text-white/60 text-sm">
                    Trabajaste {Math.floor(success.record.worked_minutes / 60)}h {success.record.worked_minutes % 60}m
                    {success.record.overtime_minutes > 0 && ` · ${success.record.overtime_minutes} min extra`}
                </p>
            )}
            <button
                data-testid="success-done"
                onClick={onDone}
                className="mt-6 px-8 py-3 rounded-full bg-white text-black font-bold uppercase tracking-widest text-xs hover:scale-105 transition"
            >
                Listo
            </button>
        </div>
    );
}
